import React from "react";
import {
  Box,
  Button,
  Modal as MuiModal,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import SVGCross from "@/assets/icons/SVGCross.tsx";

const BoxWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: "400px",
  width: "100%",
  backgroundColor: "#fff",
  padding: "32px",
  borderRadius: "24px",
  minWidth: "340px",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  maxHeight: "calc(100% - 50px)",
  zIndex: 1501,
  [theme.breakpoints.down("lg")]: {
    width: "auto",
    padding: "20px",
  },
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "24px",
  color: "#071D49",
  marginBottom: "16px",
  textAlign: "center",
  [theme.breakpoints.down("lg")]: {
    fontSize: "20px",
  },
}));

const MessageTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: "16px",
  color: "#666666",
  marginBottom: "32px",
  textAlign: "center",
  lineHeight: 1.5,
  [theme.breakpoints.down("lg")]: {
    fontSize: "14px",
  },
}));

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonColor?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Yes",
  cancelText = "No",
  confirmButtonColor,
}) => {
  const theme = useTheme();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1500 }}
      disablePortal={false}
    >
      <BoxWrapper>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <Button
            sx={{
              width: "48px",
              height: "48px",
              minWidth: "initial",
              padding: 0,
              borderColor: theme.palette.linkText,
              borderWidth: "1.5px",
              borderStyle: "solid",
              borderRadius: "4px",
            }}
            onClick={onClose}
            variant="outlined"
          >
            <SVGCross color={theme.palette.linkText} />
          </Button>
        </Box>

        <TitleTypography>{title}</TitleTypography>
        <MessageTypography>{message}</MessageTypography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "16px",
              textTransform: "none",
              border: "2px solid #0076CE",
              color: "#0076CE",
              backgroundColor: "transparent",
              "&:hover": {
                border: "2px solid #0056a3",
                backgroundColor: "rgba(0, 118, 206, 0.04)",
              },
            }}
          >
            {cancelText}
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirm}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "16px",
              textTransform: "none",
              backgroundColor: confirmButtonColor || theme.palette.red1,
              border: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  confirmButtonColor === "#DC3545" ||
                  confirmButtonColor === theme.palette.red1
                    ? theme.palette.red
                    : theme.palette.blue,
                boxShadow: "none",
              },
            }}
          >
            {confirmText}
          </Button>
        </Box>
      </BoxWrapper>
    </MuiModal>
  );
};

export default ConfirmationModal;
